import type { PropType } from 'vue';
import type { TTypographyProps } from './TTypography.types';
import { get, merge } from 'es-toolkit/compat';
import { computed, defineComponent, h } from 'vue';
import { useTheme } from '../../composables/theme';
import { useStyles } from './TTypography.styles';
import { defaultMapping } from './TTypography.utils';
import { parseAllMarkdown } from './utils/markdown/parseAllMarkdown';

const defaultProps = {
  fontStyle: 'normal',
  fontWeight: 'regular',
  variant: 'b0',
  color: '#373737',
};

export const TTypography = defineComponent({
  name: 'TTypography',
  props: {
    fontStyle: String as PropType<TTypographyProps['fontStyle']>,
    fontWeight: String as PropType<TTypographyProps['fontWeight']>,
    variant: String as PropType<TTypographyProps['variant']>,
    as: String as PropType<TTypographyProps['as']>,
    color: String as PropType<TTypographyProps['color']>,
  },
  setup(_props, { attrs, slots }) {
    const props = computed(() => merge({}, defaultProps, _props, attrs));
    const theme = useTheme();
    const color = computed(() => (get(theme.value.colors, props.value.color) as string)
      || theme.value.typography.color);
    const baseClass = useStyles(color.value);
    const classes = computed(() => [
      baseClass.value,
      `${baseClass.value}--${props.value.variant}`,
      `${baseClass.value}--${props.value.fontWeight}`,
      `${baseClass.value}--${props.value.fontStyle}`,
    ]);

    const tag = computed(() => props.value.as || defaultMapping[props.value.variant]);

    return () => {
      const slotContent = slots.default?.();
      if (!slotContent) {
        return h(tag.value, { class: classes.value });
      }

      if (!theme.value.parseMarkdown) {
        return h(tag.value, { class: classes.value }, slotContent);
      }

      const processedContent = parseAllMarkdown(slotContent);
      return h(tag.value, { class: classes.value }, processedContent);
    };
  },
});
